import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Input } from '@repo/ui'
import { SelectField } from 'components'
import type { Company } from 'types'

const CANCEL_REASONS = [
  { label: 'Too expensive', value: 'Too expensive' },
  { label: 'Switching to another product', value: 'Switching' },
  { label: 'No longer needed', value: 'No longer needed' },
  { label: 'Other', value: 'Other' },
]

interface CancelSubscriptionInternalModalProps {
  company: Company | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const CancelSubscriptionInternalModal = (
  props: CancelSubscriptionInternalModalProps,
) => {
  const { company, open, onClose = () => {}, refetch } = props

  const { open: notification } = useSnackbar()

  const [reason, setReason] = useState('')
  const [feedback, setFeedback] = useState('')

  const { cancelSubscription, isCancellingSubscription } =
    useCompanyMutationsQuery({
      onSuccess: () => {
        handleClose()
        refetch()
      },
      onError: error => {
        const message = getErrorMessage(error)
        notification({
          type: 'error',
          title: message,
        })
      },
    })

  const handleConfirm = () => {
    if (!company?.id || !reason) return
    cancelSubscription({
      companyId: company.id,
      reason,
      feedback: feedback.trim() || undefined,
    })
  }

  const handleClose = () => {
    setReason('')
    setFeedback('')
    onClose()
  }

  return (
    <Modal
      title="Cancel subscription"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isCancellingSubscription}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleConfirm}
            disabled={isCancellingSubscription || !reason}
          >
            {isCancellingSubscription
              ? 'Cancelling...'
              : 'Cancel subscription'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary">
          The subscription stays active until the end of the current billing
          period, then cancels. Access continues until then.
        </Typography>

        <SelectField
          label="Reason"
          value={reason}
          onChange={setReason}
          options={CANCEL_REASONS}
        />

        <Input
          label="Additional feedback (optional)"
          placeholder="Share additional context…"
          value={feedback}
          onValueChange={setFeedback}
          multiline
          minRows={2}
        />
      </Stack>
    </Modal>
  )
}
